// @ts-check
/**
 * IndividualDetail — peace-hub-aesthetic detail surface for individuals
 * (contacts) composed over peace-os edit infrastructure (Block 6.F.3).
 *
 * Replaces the prior `IndividualDetail.tsx` (Block 6.A read-surface +
 * 6.B.1 edit infrastructure + 6.B.3 affiliations editor + 6.C.2
 * SuggestEditButton). Edit affordances ALL preserved per layered
 * absorption (design plan §3.1).
 *
 * peace-hub source: `peace-hub/src/components/ContactView.jsx`. Per its
 * docstring (L1-7), ContactView "Replaces both ContactModal (from
 * ResourceDetail) and ContactDetail.jsx" — ContactView is the canonical
 * port target; ContactDetail (legacy modal) is dead code.
 *
 * Adapters per design plan §6 (every deviation named):
 *   - currentOrgId/currentOrgName/currentOrgOverride string-fallback
 *     paths DROPPED (peace-os has full M:N relational data).
 *   - affiliations[] from getIndividual is the relational shape; with
 *     is_primary star + role inline (peace-os additions over peace-hub).
 *   - sticky secondary header DROPPED (AppShell β2-b owns chrome).
 *   - onBack/userName/onToast props DROPPED.
 *   - FeedbackForm DROPPED.
 *
 * F-A: kind rendered slug-as-is. F-B: AffiliationCard formats date
 * strings, stripping T00:00:00.000Z. F1: no modals. F3: no per-field
 * save buttons. F4: dotted underline cue. F10: validation never
 * disables save. Q5 disposition: explicit "Verify now" button.
 */

import { useId, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import { canAddNotes, canChangeDirectoryNote, canDeleteProviderNote, canDirectWrite, canRecommendProvider, canSuggest, canSeeConnectedClients as canSeeConnectedClientsFor } from '@/lib/capabilities';
import { api, ApiError } from '@/lib/api';
import { lookupsApi } from '@/lib/lookups';
import { MultiSelectCheckbox } from '@/components/MultiSelectCheckbox';
import { useAutoSave } from '@/hooks/useAutoSave';
import { useUndo } from '@/lib/undo';
import { AffiliationEditorAdd, AffiliationEditorEdit } from '@/components/AffiliationEditor';
import { OrganizationPicker } from '@/components/peace-hub-port/OrganizationPicker';
import { suggestedResourcesApi } from '@/lib/suggestedResources';
import { Skeleton } from '@/components/Skeleton';
import { ErrorBanner } from '@/components/ErrorBanner';
import { EditableField } from '@/components/EditableField';
import { emailValidator, phoneValidator, requiredValidator } from '@/lib/validators';
import { formatPhoneDisplay, formatRelative } from '@/lib/format';
import { Section } from '@/components/peace-hub-port/ResourceDetail';
import { SuggestEditForm } from '@/components/SuggestEditForm';
import { ConnectedClients } from '@/components/directory/ConnectedClients';
import { PeaceTeamNotes } from '@/components/directory/PeaceTeamNotes';
import { DeleteNoteConfirm } from '@/components/directory/DeleteNoteConfirm';
import { CmWriteNoteForm } from '@/components/peace-hub-port/CmWriteNoteForm';
import { pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { BackToDirectory } from '@/components/BackToDirectory';
import { RecommendedBadge } from '@/components/RecommendedBadge';
import { Check, Phone, Mail, MapPin, Pencil, Printer } from 'lucide-react';
import { PageShell } from '@/components/primitives/PageShell';
import { Button } from '@/components/primitives/Button';
import { Chip } from '@/components/primitives/Chip';
import { ContactCard, AsideContactRow, DirectoryAddressRow } from '@/components/directory/OrgContactAside';
import { DirectoryBand, BandPersonIdentity } from '@/components/directory/DirectoryBand';
import { DirectoryRow, DashedAdd } from '@/components/directory/DirectoryRow';
import {
  ACTION_ANCHOR_ON_DARK_PRIMARY,
  ACTION_ANCHOR_ON_DARK_SECONDARY,
} from '@/components/directory/actionAnchors';

export function IndividualDetail() {
  const { tenantSlug } = useTenant();
  const { id } = useParams();

  const query = useQuery({
    queryKey: ['individual', tenantSlug, id],
    queryFn: ({ signal }) =>
      api.getIndividual(tenantSlug, /** @type {string} */ (id), signal),
    enabled: Boolean(id),
  });

  // UX-13 S4 — the detail chassis is PageShell (gate parity: no access
  // gate before, none now); headerless — EntityHeader is the page header.
  return (
    <PageShell maxWidth="xl" testId="page-individual">
      <BackToDirectory tenantSlug={tenantSlug} fallbackView="people" />

      {query.isLoading && (
        <div className="mt-4 space-y-3">
          <Skeleton width="w-2/3" height="h-6" />
          <Skeleton width="w-1/3" height="h-4" />
          <Skeleton width="w-full" height="h-32" />
        </div>
      )}

      {query.error && <ErrorBanner error={query.error} onRetry={() => query.refetch()} />}

      {query.data && id !== undefined && (
        <IndividualView individual={query.data} tenantSlug={tenantSlug} individualId={id} />
      )}
    </PageShell>
  );
}

/**
 * @param {{
 *   individual: import('@/lib/types').IndividualDetail,
 *   tenantSlug: string,
 *   individualId: string,
 * }} props
 */
function IndividualView({ individual, tenantSlug, individualId }) {
  const saveName = useEditIndividualText(tenantSlug, individualId, 'full_name', 'Name updated');
  const saveTitle = useEditIndividualText(tenantSlug, individualId, 'title', 'Title updated');
  const saveBusinessPhone = useEditIndividualText(tenantSlug, individualId, 'business_phone', 'Business phone updated');
  const saveCellPhone = useEditIndividualText(tenantSlug, individualId, 'cell_phone', 'Cell phone updated');
  const saveFax = useEditIndividualText(tenantSlug, individualId, 'fax', 'Fax updated');
  const saveEmail = useEditIndividualText(tenantSlug, individualId, 'email', 'Email updated');
  const saveAddress = useSaveIndividualAddress(tenantSlug, individualId);
  const saveSpecialties = useEditIndividualText(tenantSlug, individualId, 'specialties', 'Specialties updated');
  const saveNotes = useEditIndividualText(tenantSlug, individualId, 'notes', 'Notes updated');

  // X.6.6-R — gate inline-edit on the real canDirectWrite capability (parity
  // with OrganizationDetail; this page previously left every EditableField
  // ungated). CM read-only; admin hover-to-edit. canSuggest drives the
  // Suggest-a-change trigger (visible to write + non-write roles).
  const { whoami } = useTenant();
  const queryClient = useQueryClient();
  const canEdit = canDirectWrite(whoami);
  const canSuggestChange = canSuggest(whoami);
  // Directory care-web: the Connected clients section is a clinical
  // cross-reference, so it mounts only for a viewer who reads every client
  // (canSeeConnectedClients: every client, unless the role is fenced from
  // them), mirroring the server route's gate. The directory page itself stays open to all roles.
  const canSeeConnectedClients = canSeeConnectedClientsFor(whoami);
  const [suggestOpen, setSuggestOpen] = useState(false);

  // Team notes on the person (individual_notes, 0242): the organization
  // page's notes, wired the same way. Add and edit gate on add_notes;
  // Delete shows on any note to a holder of delete_provider_note and on
  // her own to an add_notes holder (the server's door and gate).
  const canAddNote = canAddNotes(whoami);
  const canDeleteAny = canDeleteProviderNote(whoami);
  const contact = useSupportContact();
  const notesKey = ['individual-notes', tenantSlug, individualId];
  const notesQuery = useQuery({
    queryKey: notesKey,
    queryFn: ({ signal }) => api.listIndividualNotes(tenantSlug, individualId, signal),
  });
  const [isWriteNoteOpen, setIsWriteNoteOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(
    /** @type {import('@/lib/types').IndividualNote | null} */ (null),
  );
  const [deletingNote, setDeletingNote] = useState(
    /** @type {import('@/lib/types').IndividualNote | null} */ (null),
  );
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(/** @type {string | null} */ (null));
  const onConfirmDelete = async () => {
    if (!deletingNote || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.deleteIndividualNote(tenantSlug, individualId, deletingNote.id);
      await queryClient.invalidateQueries({ queryKey: ['individual-notes', tenantSlug, individualId] });
      setDeletingNote(null);
    } catch (err) {
      setDeleteError(pageWriteText(err, contact));
    } finally {
      setDeleteBusy(false);
    }
  };
  const onCancelDelete = () => {
    if (deleteBusy) return;
    setDeletingNote(null);
    setDeleteError(null);
  };

  // The band's h1 aria-labelledby points straight at this control's own
  // id when it's mounted (see DirectoryBand's docstring), a nested
  // control's aria-label is not reachable by ordinary content recursion.
  const nameFieldId = useId();

  // Recommended people (0197): the same badge and toggle as an organization.
  // The button shows only to roles holding recommend_provider (the server
  // 403s everyone else); the badge shows to everyone once set. Invalidating
  // the search list refreshes the directory card and the recommended-first
  // sort too.
  const canRecommend = canRecommendProvider(whoami);
  const [recommendBusy, setRecommendBusy] = useState(false);
  const onToggleRecommendation = async () => {
    if (recommendBusy) return;
    setRecommendBusy(true);
    try {
      await api.setIndividualRecommendation(tenantSlug, individualId, !individual.is_recommended);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['individual', tenantSlug, individualId] }),
        queryClient.invalidateQueries({ queryKey: ['individuals-for-search', tenantSlug] }),
      ]);
    } catch (err) {
      console.error('setIndividualRecommendation failed', err);
    } finally {
      setRecommendBusy(false);
    }
  };

  // Phase 1.7.3 — specialties picker state. Picker mirrors
  // SuggestResourceForm individual-mode shape (multi-select against
  // provider_specialties lookup; display-name strings; serialize to
  // comma-joined free-text on save per the existing `specialties` column
  // shape — schema H1 free-text preserved). Lookup-promotion remains
  // deferred to Phase 2; not mentioned in user-facing UI.
  const [editingSpecialties, setEditingSpecialties] = useState(false);
  const [specPickerValues, setSpecPickerValues] = useState(/** @type {string[]} */ ([]));
  const specsQuery = useQuery({
    queryKey: ['lookups', 'specialties', tenantSlug],
    queryFn: ({ signal }) => lookupsApi.listSpecialties(tenantSlug, { limit: 500 }, signal),
    enabled: editingSpecialties,
    staleTime: 5 * 60 * 1000,
  });
  const openSpecialtiesEditor = () => {
    const tokens = (individual.specialties ?? '')
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    setSpecPickerValues(tokens);
    setEditingSpecialties(true);
  };
  const handleSaveSpecialties = async () => {
    // Use empty string for the cleared case — matches EditableField's
    // wire shape and satisfies useEditIndividualText's `string` typing.
    // api.updateIndividual treats '' as "clear" at the column boundary.
    const serialized =
      specPickerValues.length > 0 ? specPickerValues.join(', ') : '';
    await saveSpecialties(serialized, individual.specialties ?? '');
    setEditingSpecialties(false);
  };

  // Phone preferred for the Call action button: business_phone first,
  // cell_phone fallback (peace-hub ContactView L107-113 pattern).
  const callPhone = individual.business_phone ?? individual.cell_phone;

  return (
    <div className="mt-4">
      {/* Forest personality band (Directory detail rebuild §1). The
          admin-mode banner is retired — admin-ness is expressed by the edit
          affordances themselves. The editable title rides the subline; the
          referral-partner tag rides the band's gold tag slot. */}
      <DirectoryBand
        kind="person"
        identity={<BandPersonIdentity />}
        name={individual.full_name}
        nameSlot={
          canEdit ? (
            <span className="[&_button]:text-cream [&_button:hover]:bg-cream/10 [&_button]:focus-visible:ring-cream/60 [&_[data-saving]]:text-cream/80">
              <EditableField id={nameFieldId} value={individual.full_name} onSave={saveName} label="Full name" validate={requiredValidator} canEdit={canEdit} />
            </span>
          ) : undefined
        }
        nameSlotId={canEdit ? nameFieldId : undefined}
        subline={
          canEdit ? (
            <span className="[&_button]:text-cream/85 [&_button:hover]:bg-cream/10 [&_button]:focus-visible:ring-cream/60 [&_[data-saving]]:text-cream/80">
              <EditableField value={individual.title} onSave={saveTitle} label="Title" placeholder="Add title" canEdit={canEdit} />
            </span>
          ) : undefined
        }
        purpose={individual.title ?? undefined}
        tag={
          individual.is_referral_partner ? (
            <span className="inline-flex items-center gap-1 rounded-tight border border-warm-400/40 bg-warm-400/[18%] px-2 py-0.5 text-[11px] font-semibold text-cream">
              <Check size={12} aria-hidden="true" /> Referral partner
            </span>
          ) : undefined
        }
        updatedLabel={individual.updated_at ? `Updated ${formatRelative(individual.updated_at)}` : undefined}
        badge={
          individual.is_recommended ? (
            <RecommendedBadge
              tone="onDark"
              isRecommended={individual.is_recommended}
              recommendedAt={individual.recommended_at}
              recommendedByName={individual.recommended_by_display_name}
            />
          ) : undefined
        }
        actions={
          <>
            {callPhone && (
              <a href={`tel:${callPhone.replace(/[^\d+]/g, '')}`} className={ACTION_ANCHOR_ON_DARK_PRIMARY}>
                <Phone size={16} aria-hidden="true" /> Call
              </a>
            )}
            {individual.email && (
              <a href={`mailto:${individual.email}`} className={`${ACTION_ANCHOR_ON_DARK_SECONDARY} max-w-[200px] truncate`}>
                <Mail size={16} aria-hidden="true" /> Email
              </a>
            )}
            {canSuggestChange && (
              <Button
                variant="secondary-on-dark"
                onClick={() => setSuggestOpen(true)}
                data-testid="suggest-change-button"
                icon={<Pencil size={15} aria-hidden="true" />}
              >
                Suggest a change
              </Button>
            )}
          </>
        }
      />

      {/* The steward toggle, a quiet row below the band, the same as the
          organization page. The badge moved into the band's meta line
          (contract addendum revision 4 §5 items 3, 6, 7); this row keeps
          only the toggle, and renders only for a user who can recommend
         , never gated on is_recommended, so a reader never sees an
          orphan row. */}
      {canRecommend && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="individual-recommendation-row">
          <Button
            variant="secondary"
            size="sm"
            onClick={onToggleRecommendation}
            disabled={recommendBusy}
            data-testid="recommend-toggle"
          >
            {individual.is_recommended ? 'Remove recommendation' : 'Mark as recommended'}
          </Button>
        </div>
      )}

      {/* X.6.6-R — two-column premium body (single column below lg). */}
      <div className="mt-6 grid items-start gap-6 lg:grid-cols-[1fr_320px]">
        {/* LEFT column */}
        <div className="flex min-w-0 flex-col gap-4">
          {/* "From the Peace team": the person's team notes, the organization
              page's card and wiring (add, edit, delete, anonymous, type).
              The person's older free-text notes field stays, as the card's
              standing note after the dated ones: an editor edits it in place
              (EditableField, labelled with the card title), a reader reads
              it. The empty sentence shows only when there is neither. */}
          <PeaceTeamNotes
            notes={notesQuery.data?.rows ?? []}
            providerName={individual.full_name}
            onAddNote={canAddNote ? () => setIsWriteNoteOpen(true) : null}
            onEditNote={
              canDeleteAny || canAddNote
                ? (note) => {
                    setEditingNote(note);
                    setIsWriteNoteOpen(true);
                  }
                : null
            }
            onDeleteNote={
              canDeleteAny || canAddNote
                ? (note) => {
                    setDeletingNote(note);
                    setDeleteError(null);
                  }
                : null
            }
            canDeleteNote={(note) => canChangeDirectoryNote(whoami, note)}
            canEditNote={(note) => canChangeDirectoryNote(whoami, note)}
            standing={
              individual.notes ? (
                canEdit ? (
                  <div className="font-display text-sm italic leading-[1.55] text-ink">
                    <EditableField
                      value={individual.notes}
                      onSave={saveNotes}
                      label="From the Peace team"
                      multiline
                      canEdit
                    />
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap font-display text-sm italic leading-[1.55] text-ink">
                    {individual.notes}
                  </p>
                )
              ) : null
            }
          />

          {/* Specialties — read-only pills for CM; multi-select editor for admin. */}
          <Section title="Specialties">
            {canEdit && editingSpecialties ? (
              <div>
                <MultiSelectCheckbox
                  options={specsQuery.data?.rows ?? []}
                  getOptionValue={(r) => r.display_name}
                  getOptionLabel={(r) => r.display_name}
                  selectedValues={specPickerValues}
                  onChange={setSpecPickerValues}
                  loading={specsQuery.isLoading}
                  ariaLabel="Specialties"
                />
                <div className="mt-2 flex gap-2">
                  <Button onClick={handleSaveSpecialties}>Save</Button>
                  <Button variant="ghost" onClick={() => setEditingSpecialties(false)}>Cancel</Button>
                </div>
              </div>
            ) : canEdit ? (
              <button type="button" onClick={openSpecialtiesEditor} className="w-full text-left" aria-label="Edit specialties">
                {individual.specialties ? (
                  <SpecialtyPills value={individual.specialties} />
                ) : (
                  <span className="text-sm italic text-ink-soft">Tap to add a specialty.</span>
                )}
              </button>
            ) : individual.specialties ? (
              <SpecialtyPills value={individual.specialties} />
            ) : (
              <span className="text-sm italic text-ink-soft">Once someone adds a specialty, you'll see it here.</span>
            )}
          </Section>

          {/* Affiliations — organizations this person is linked to, as the
              shared linked-row system (Directory detail rebuild §5). */}
          <AffiliationsEditor
            tenantSlug={tenantSlug}
            individualId={individualId}
            affiliations={individual.affiliations}
            canEdit={canEdit}
          />

          {/* Connected clients: the care-web reverse read. Mounted only
              for canReadEveryClient (denied roles fire no fetch and see no
              section; the server gate mirrors this). */}
          {canSeeConnectedClients && (
            <ConnectedClients
              tenantSlug={tenantSlug}
              subject={{ kind: 'individual', id: individualId }}
              name={individual.full_name}
            />
          )}
        </div>

        {/* RIGHT sticky aside — contact card + (admin) flags + See-something-off. */}
        <aside className="flex flex-col gap-4 lg:sticky lg:top-[84px]">
          <ContactCard>
            <AsideContactRow icon={Phone} label="Business phone" emphasis>
              <EditableField value={individual.business_phone} onSave={saveBusinessPhone} label="Business phone" validate={phoneValidator} placeholder="Add phone" format={formatPhoneDisplay} canEdit={canEdit} />
            </AsideContactRow>
            <AsideContactRow icon={Phone} label="Cell phone" emphasis>
              <EditableField value={individual.cell_phone} onSave={saveCellPhone} label="Cell phone" validate={phoneValidator} placeholder="Add phone" format={formatPhoneDisplay} canEdit={canEdit} />
            </AsideContactRow>
            {/* A person's fax (0242), the organization page's row: shown when
                set, and to an editor to add one. */}
            {(individual.fax || canEdit) && (
              <AsideContactRow icon={Printer} label="Fax" emphasis>
                <EditableField value={individual.fax ?? null} onSave={saveFax} label="Fax" validate={phoneValidator} placeholder="Add fax" format={formatPhoneDisplay} canEdit={canEdit} />
              </AsideContactRow>
            )}
            <AsideContactRow icon={Mail} label="Email">
              <EditableField value={individual.email} onSave={saveEmail} label="Email" validate={emailValidator} placeholder="Add email" canEdit={canEdit} />
            </AsideContactRow>
            <AsideContactRow icon={MapPin} label="Address">
              <DirectoryAddressRow listing={individual} canEdit={canEdit} onSave={saveAddress} testId="individual-address-edit" />
            </AsideContactRow>
          </ContactCard>

          {!canEdit && canSuggestChange && (
            <div className="rounded-card border border-sage-800/[8%] bg-sand p-4">
              <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-ink-soft">See something off?</div>
              <p className="mb-3 text-[13px] leading-relaxed text-ink-soft">Title changed, or they moved practices? Let an admin know.</p>
              <Button
                variant="secondary"
                className="w-full"
                icon={<Pencil size={15} aria-hidden="true" />}
                onClick={() => setSuggestOpen(true)}
                data-testid="aside-suggest-change"
              >
                Suggest a change
              </Button>
            </div>
          )}
        </aside>
      </div>

      {canAddNote && (
        <CmWriteNoteForm
          isOpen={isWriteNoteOpen}
          onClose={() => {
            setIsWriteNoteOpen(false);
            setEditingNote(null);
          }}
          tenantSlug={tenantSlug}
          providerId={individualId}
          noteTarget="individual"
          providerName={individual.full_name}
          invalidationKey={notesKey}
          initialBody={editingNote?.body}
          initialNoteType={editingNote?.note_type}
          initialIsAnonymous={editingNote?.is_anonymous}
          editNoteId={editingNote?.id}
        />
      )}

      {deletingNote && (
        <DeleteNoteConfirm
          busy={deleteBusy}
          error={deleteError}
          errorTestId="individual-note-delete-error"
          onConfirm={onConfirmDelete}
          onCancel={onCancelDelete}
        />
      )}

      {/* X.6.6-R — one SuggestEditForm for the action-row + aside triggers
          (X.5.3 multi-field suggest / send-for-review flow byte-identical —
          same component + props the retired SuggestChangeButton passed). */}
      {canSuggestChange && (
        <SuggestEditForm
          isOpen={suggestOpen}
          onClose={() => setSuggestOpen(false)}
          tenantSlug={tenantSlug}
          targetType="individual"
          target={individual}
          invalidationKey={['individual', tenantSlug, individualId]}
        />
      )}
    </div>
  );
}

// ---- Custom hooks (top-level so rules-of-hooks linter sees a hook
//      composing useAutoSave / useMutation cleanly).

/**
 * The address's save: PATCH the parts, then re-read the person. Answers
 * the save's geocode outcome, which the postal dialog reports
 * ('shown_with_organization' for a person on an organization).
 *
 * @param {string} tenantSlug
 * @param {string} individualId
 * @returns {(parts: import('@/components/clientRecord/AddressesCard').AddressFormParts) => Promise<import('@/components/clientRecord/AddressesCard').PostalSaveOutcome>}
 */
function useSaveIndividualAddress(tenantSlug, individualId) {
  const queryClient = useQueryClient();
  return async (parts) => {
    const saved = await api.updateIndividual(tenantSlug, individualId, { address: parts });
    await queryClient.invalidateQueries({ queryKey: ['individual', tenantSlug, individualId] });
    return saved.geocode_outcome ?? 'not_attempted';
  };
}

/**
 * @param {string} tenantSlug
 * @param {string} individualId
 * @param {'full_name'|'title'|'business_phone'|'cell_phone'|'fax'|'email'|'address'|'specialties'|'notes'} field
 * @param {string} undoLabel
 * @returns {(newValue: string, priorValue: string) => Promise<void>}
 */
function useEditIndividualText(tenantSlug, individualId, field, undoLabel) {
  const result = useAutoSave({
    queryKey: ['individual', tenantSlug, individualId],
    /** @param {string} newValue */
    mutationFn: (newValue) =>
      api.updateIndividual(tenantSlug, individualId, { [field]: newValue }),
    /**
     * @param {import('@/lib/types').IndividualDetail | undefined} prev
     * @param {string} newValue
     */
    onOptimisticUpdate: (prev, newValue) =>
      prev ? { ...prev, [field]: newValue } : prev,
    /** @param {string} priorValue */
    undoMutationFn: (priorValue) =>
      api.updateIndividual(tenantSlug, individualId, { [field]: priorValue }),
    undoLabel,
  });
  return result.save;
}



// ---- Affiliations editor sub-component (Block 6.B.3) ----
// JS port of the TS impl. State machine: closed → searching → filling → closed.

/**
 * @param {{
 *   tenantSlug: string,
 *   individualId: string,
 *   affiliations: Array<import('@/lib/types').AffiliationWithOrg>,
 *   canEdit: boolean,
 * }} props
 */
function AffiliationsEditor({ tenantSlug, individualId, affiliations, canEdit }) {
  const queryClient = useQueryClient();
  const { push: pushUndo } = useUndo();
  const [addState, setAddState] = useState(
    /** @type {{ kind: 'closed' } | { kind: 'searching' } | { kind: 'filling', org: { id: string, display_name: string } }} */
    ({ kind: 'closed' })
  );
  const [conflictMsg, setConflictMsg] = useState(/** @type {string | null} */ (null));
  // Phase 1.7.3 — feedback line for the inline-create new-org flow.
  // Surfaced after a successful suggestedResourcesApi.submit; cleared on
  // next addState change or auto-cleared after 6s.
  const [newOrgToast, setNewOrgToast] = useState(/** @type {string | null} */ (null));
  // Directory detail rebuild §5 — the affiliation being edited inline (its
  // provider_id; null = none) and the row whose primary star is toggling.
  const [editingId, setEditingId] = useState(/** @type {string | null} */ (null));
  const [primaryBusyId, setPrimaryBusyId] = useState(/** @type {string | null} */ (null));

  const queryKey = ['individual', tenantSlug, individualId];
  const refetch = async () => {
    await queryClient.invalidateQueries({ queryKey });
  };

  /** @param {unknown} err */
  function handleConflict(err) {
    if (err instanceof ApiError && err.status === 409) {
      setConflictMsg(
        "Someone else just changed this person's main organization. Press Refresh to see it.",
      );
    } else {
      throw err;
    }
  }

  /** @param {import('@/lib/types').AffiliationWithOrg} aff */
  function buildEditCallbacks(aff) {
    return {
      /** @param {{ role: string | null, title: string | null, start_date: string | null, end_date: string | null }} patch */
      onSave: async (patch) => {
        const prior = {
          role: aff.role,
          title: aff.title,
          start_date: aff.start_date,
          end_date: aff.end_date,
        };
        await api.updateAffiliation(tenantSlug, individualId, aff.provider_id, patch);
        await refetch();
        pushUndo({
          label: `Affiliation updated · ${aff.provider_name}`,
          undo: async () => {
            await api.updateAffiliation(tenantSlug, individualId, aff.provider_id, prior);
            await refetch();
          },
        });
      },
      onSetPrimary: async () => {
        try {
          await api.setPrimaryAffiliation(tenantSlug, individualId, aff.provider_id);
          await refetch();
        } catch (err) {
          handleConflict(err);
        }
      },
      onClearPrimary: async () => {
        await api.clearPrimaryAffiliation(tenantSlug, individualId, aff.provider_id);
        await refetch();
      },
      onRemove: async () => {
        const snapshot = { ...aff };
        await api.deleteAffiliation(tenantSlug, individualId, aff.provider_id);
        await refetch();
        pushUndo({
          label: `Removed affiliation · ${snapshot.provider_name}`,
          undo: async () => {
            await api.createAffiliation(tenantSlug, {
              provider_individual_id: individualId,
              provider_id: snapshot.provider_id,
              role: snapshot.role,
              title: snapshot.title,
              is_primary: snapshot.is_primary,
              start_date: snapshot.start_date,
              end_date: snapshot.end_date,
            });
            await refetch();
          },
        });
      },
    };
  }

  return (
    <section className="rounded-card border border-sage-800/[8%] bg-paper p-4 shadow-sh1 sm:p-5">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="font-display text-lg text-sage-800">Affiliations</h2>
        {affiliations.length > 0 && (
          <span className="rounded-pill bg-sage-50 px-2 py-0.5 text-xs font-semibold text-sage-400">
            {affiliations.length}
          </span>
        )}
      </div>

      {affiliations.length === 0 && addState.kind === 'closed' && (
        <p className="mb-2 text-sm text-ink-soft">
          {canEdit
            ? 'Add an affiliation to link this contact to an organization.'
            : "Once someone adds an affiliation, you'll see it here."}
        </p>
      )}

      {affiliations.length > 0 && (
        <div className="divide-y divide-sage-800/[8%]">
          {affiliations.map((aff) => {
            const cb = canEdit ? buildEditCallbacks(aff) : undefined;
            if (editingId === aff.provider_id && cb) {
              return (
                <div key={aff.provider_id} className="py-2">
                  <AffiliationEditorEdit
                    affiliation={aff}
                    onSave={async (patch) => {
                      await cb.onSave(patch);
                      setEditingId(null);
                    }}
                    onCancel={() => setEditingId(null)}
                  />
                </div>
              );
            }
            const meta = [
              aff.role,
              aff.title,
              aff.is_primary ? 'Primary affiliation' : null,
            ].filter(Boolean);
            return (
              <DirectoryRow
                key={aff.provider_id}
                to={`/t/${encodeURIComponent(tenantSlug)}/organizations/${encodeURIComponent(aff.provider_id)}`}
                avatar="org"
                name={aff.provider_name}
                meta={meta}
                starToggle={
                  cb
                    ? {
                        active: aff.is_primary,
                        busy: primaryBusyId === aff.provider_id,
                        onToggle: async () => {
                          setPrimaryBusyId(aff.provider_id);
                          try {
                            if (aff.is_primary) await cb.onClearPrimary();
                            else await cb.onSetPrimary();
                          } finally {
                            setPrimaryBusyId(null);
                          }
                        },
                      }
                    : undefined
                }
                primary={!cb && aff.is_primary}
                onEdit={cb ? () => setEditingId(aff.provider_id) : undefined}
                onRemove={cb ? () => void cb.onRemove() : undefined}
                editAriaLabel={`Edit affiliation with ${aff.provider_name}`}
                removeAriaLabel={`Remove affiliation with ${aff.provider_name}`}
              />
            );
          })}
        </div>
      )}

      {conflictMsg && (
        <div role="alert" className="my-2 rounded-card border border-warm-300 bg-warm-50 p-2 text-xs text-warm-700">
          {conflictMsg}
          <button
            type="button"
            onClick={() => {
              setConflictMsg(null);
              void refetch();
            }}
            className="ml-2 min-h-11 px-2 py-1 underline"
          >
            Refresh
          </button>
        </div>
      )}

      {canEdit && addState.kind === 'closed' && (
        <div className="mt-3">
          <DashedAdd label="Add affiliation" onClick={() => setAddState({ kind: 'searching' })} />
        </div>
      )}

      {canEdit && addState.kind === 'searching' && (
        <div className="mt-2">
          <OrganizationPicker
            onPickExisting={(org) => setAddState({ kind: 'filling', org })}
            onInlineSubmit={async (payload) => {
              // Phase 1.7.3 — inline-create new org routes through
              // suggestedResourcesApi.submit with contact_individual_id
              // pointing at the current individual. applySuggestedResource
              // (existing infrastructure) creates the org + a role='Contact'
              // affiliation linking back to this individual at approve-time.
              // Operator's role/title/dates intent for the new-org
              // affiliation is NOT carried through in this iteration —
              // Phase 1.8 may extend (see OrganizationPicker docstring).
              await suggestedResourcesApi.submit(tenantSlug, {
                target_type: 'organization',
                payload: {
                  ...payload,
                  contact_individual_id: individualId,
                },
              });
              await queryClient.invalidateQueries({
                queryKey: ['admin-review', 'badge-count'],
              });
              setNewOrgToast(
                `New organization "${payload.name}" submitted for steward review. The affiliation will appear once approved.`,
              );
              setAddState({ kind: 'closed' });
              setTimeout(() => setNewOrgToast(null), 6000);
            }}
            onCancel={() => setAddState({ kind: 'closed' })}
          />
        </div>
      )}

      {newOrgToast && (
        <div
          role="status"
          aria-live="polite"
          className="mt-2 rounded-base border border-sage-200 bg-sage-50 p-2 text-xs text-sage-800"
        >
          {newOrgToast}
        </div>
      )}

      {canEdit && addState.kind === 'filling' && (
        <div className="mt-2">
          <AffiliationEditorAdd
            org={addState.org}
            onAdd={async (input) => {
              const orgRef = addState.org;
              await api.createAffiliation(tenantSlug, {
                provider_individual_id: individualId,
                provider_id: orgRef.id,
                role: input.role,
                title: input.title,
                is_primary: input.is_primary,
                start_date: input.start_date,
                end_date: input.end_date,
              });
              await refetch();
              setAddState({ kind: 'closed' });
              pushUndo({
                label: `Added affiliation · ${orgRef.display_name}`,
                undo: async () => {
                  await api.deleteAffiliation(tenantSlug, individualId, orgRef.id);
                  await refetch();
                },
              });
            }}
            onCancel={() => setAddState({ kind: 'closed' })}
          />
        </div>
      )}
    </section>
  );
}

/**
 * Phase 1.7.1 — Specialty pill renderer (Block 6.G.4 — aligned to
 * peace-hub canonical). Splits a comma-joined free-text string into
 * pill tokens. Renders nothing when value is null / empty.
 *
 * Block 6.G.4 — canonical spec per peace-hub `ResourceDetail.jsx:322`:
 *   `px-2.5 py-1 rounded-lg text-xs font-medium bg-warm-100 text-warm-600`
 *
 * Specialties belong to the warm family in peace-hub canonical
 * (clinical specialty class; semantic differentiation from sage
 * service-types). Phase 1.7.1's `warm-50 / warm-700 / rounded-full`
 * was close-but-not-canonical; this aligns exactly. Borderless,
 * `rounded-lg` (not `rounded-full`), text-xs (not text-sm).
 *
 * @param {{ value: string | null }} props
 */
function SpecialtyPills({ value }) {
  // UX-13 S4 — specialties wear the house specialty Chip (§4: one taxonomy,
  // one treatment — the same warm pair the org surfaces render), replacing
  // the page-local sand pill.
  if (!value) return null;
  const tokens = value
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  if (tokens.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {tokens.map((token, i) => (
        <Chip key={`${token}-${i}`} variant="specialty">
          {token}
        </Chip>
      ))}
    </div>
  );
}

